/* ElbowReliever UI — countdown, delivery estimate, bundle cards (price,
   savings, gift bar, stock, button price), add-ons in the same click, sticky
   add to cart, review highlights, and motion (motion.dev vanilla build,
   window.Motion). Every effect has a no-motion path: with
   prefers-reduced-motion the page is fully static and readable.
   Cart/checkout always go through Shopify's own product form. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const M = window.Motion;
  const canAnimate = !!M && !reduceMotion;

  /* ---------- UK time helpers ---------- */
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  // window.__erNow (ISO string) lets tests pin "now".
  const now = () => (window.__erNow ? new Date(window.__erNow) : new Date());
  const ukParts = (d = now()) => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(d);
    const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
    return {
      date: Date.UTC(+p.year, +p.month - 1, +p.day), // UK calendar day as a UTC midnight
      seconds: (+p.hour % 24) * 3600 + +p.minute * 60 + +p.second,
    };
  };
  const DAY_MS = 864e5;
  const iso = (t) => new Date(t).toISOString().slice(0, 10);
  const fmtDay = (t) => {
    const d = new Date(t);
    return `${DAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
  };
  const pad = (n) => String(n).padStart(2, '0');

  /* ---------- Countdown ---------- */
  class ErCountdown extends HTMLElement {
    connectedCallback() {
      this.h = this.querySelector('[data-h]');
      this.m = this.querySelector('[data-m]');
      this.s = this.querySelector('[data-s]');
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    target() {
      if (this.dataset.mode === 'fixed' && this.dataset.end) {
        const t = Date.parse(this.dataset.end);
        return Number.isNaN(t) ? null : t;
      }
      // Next midnight in UK time.
      const { seconds } = ukParts();
      return now().getTime() + (86400 - seconds) * 1000;
    }
    tick() {
      const end = this.target();
      if (!end) return;
      let diff = Math.max(0, end - now().getTime());
      if (diff === 0 && this.dataset.mode === 'fixed') {
        const host = this.closest('.er-offer, .er-announce__offer');
        (host || this).hidden = true;
        clearInterval(this.timer);
        return;
      }
      const h = Math.floor(diff / 3.6e6);
      diff -= h * 3.6e6;
      const m = Math.floor(diff / 6e4);
      diff -= m * 6e4;
      const s = Math.floor(diff / 1000);
      this.h.textContent = pad(h);
      this.m.textContent = pad(m);
      this.s.textContent = pad(s);
    }
  }
  if (!customElements.get('er-countdown')) customElements.define('er-countdown', ErCountdown);

  /* ---------- Delivery estimate (real settings only) ---------- */
  class ErDelivery extends HTMLElement {
    connectedCallback() {
      this.cutoff = Number(this.dataset.cutoff || 14) * 3600;
      this.days = (this.dataset.days || '1,2,3,4,5').split(',').map((x) => Number(x.trim()));
      this.min = Number(this.dataset.min || 1);
      this.max = Math.max(this.min, Number(this.dataset.max || 3));
      this.holidays = new Set((this.dataset.holidays || '').split(',').map((x) => x.trim()).filter(Boolean));
      this.line = this.querySelector('[data-er-deliv-line]');
      this.ordered = this.querySelector('[data-er-d-ordered]');
      this.dispatch = this.querySelector('[data-er-d-dispatch]');
      this.arrive = this.querySelector('[data-er-d-arrive]');
      this.render();
      this.timer = setInterval(() => this.render(), 1000);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    isDispatchDay(t) {
      const dow = new Date(t).getUTCDay() || 7;
      return this.days.includes(dow) && !this.holidays.has(iso(t));
    }
    isWorkingDay(t) {
      const dow = new Date(t).getUTCDay();
      return dow !== 0 && dow !== 6 && !this.holidays.has(iso(t));
    }
    addWorkingDays(t, n) {
      let d = t;
      let left = n;
      while (left > 0) {
        d += DAY_MS;
        if (this.isWorkingDay(d)) left -= 1;
      }
      return d;
    }
    render() {
      const { date, seconds } = ukParts();
      if (this.dataset.mode === 'countdown') {
        // Countdown only (no delivery dates): "Order within 14h 32m 10s and we process your order today".
        if (this.isDispatchDay(date) && seconds < this.cutoff) {
          const left = this.cutoff - seconds;
          const h = Math.floor(left / 3600);
          const m = Math.floor((left % 3600) / 60);
          const s = left % 60;
          this.line.innerHTML = `Order within <strong class="er-delivery__timer">${h}h ${pad(m)}m ${pad(s)}s</strong> ${this.dataset.suffix || ''}`;
        } else {
          this.line.textContent = this.dataset.fallback || '';
        }
        return;
      }
      let dispatch = date;
      let sameDay = false;
      if (this.isDispatchDay(date) && seconds < this.cutoff) {
        sameDay = true;
      } else {
        do dispatch += DAY_MS;
        while (!this.isDispatchDay(dispatch));
      }
      const first = this.addWorkingDays(dispatch, this.min);
      const last = this.addWorkingDays(dispatch, this.max);
      const range = first === last ? fmtDay(first) : `${fmtDay(first)} – ${fmtDay(last)}`;
      // Compact form for the small step labels: "29 Sep – 1 Oct".
      const short = (t) => fmtDay(t).slice(4);
      const rangeShort = first === last ? fmtDay(first) : `${short(first)} – ${short(last)}`;
      if (sameDay) {
        const left = this.cutoff - seconds;
        const h = Math.floor(left / 3600);
        const m = Math.floor((left % 3600) / 60);
        const s = left % 60;
        this.line.innerHTML = `Order within <strong class="er-delivery__timer">${h}h ${pad(m)}m ${pad(s)}s</strong> to get it <strong>${range}</strong>`;
      } else {
        this.line.innerHTML = `Order now to get it <strong>${range}</strong>`;
      }
      if (this.ordered) this.ordered.textContent = `Today, ${fmtDay(date).slice(4)}`;
      if (this.dispatch) this.dispatch.textContent = sameDay ? 'Today' : fmtDay(dispatch);
      if (this.arrive) this.arrive.textContent = rangeShort;
    }
  }
  if (!customElements.get('er-delivery')) customElements.define('er-delivery', ErDelivery);

  /* ---------- Bundle cards → Shopify variant ---------- */
  const setText = (el, value) => {
    if (!el) return;
    el.textContent = value || '';
    el.hidden = !value;
  };

  const setVariant = (sectionId, input, { updateUrl = true } = {}) => {
    const form = document.getElementById(`product-form-${sectionId}`);
    if (!form) return;
    const d = input.dataset;
    const idInput = form.querySelector('input[name="id"]');
    if (idInput) {
      idInput.value = input.value;
      idInput.disabled = false;
    }
    // What is in the pack travels with the line item (cart, checkout, order, CJ).
    [form, ...document.querySelectorAll('.er-final-cta__form')].forEach((f) => {
      let incl = f.querySelector('input[data-er-contents]');
      if (!incl) {
        incl = document.createElement('input');
        incl.type = 'hidden';
        incl.name = 'properties[Includes]';
        incl.dataset.erContents = '';
        f.appendChild(incl);
      }
      incl.value = d.contents || '';
      incl.disabled = !d.contents;
    });
    // Keep Shopify's payment-terms form and our final CTA form on the same variant.
    document
      .querySelectorAll(`#product-form-installment-${sectionId} input[name="id"], .er-final-cta__form input[name="id"]`)
      .forEach((el) => (el.value = input.value));
    document.querySelectorAll('.er-final-cta__price').forEach((el) => (el.textContent = d.price));

    // Price + savings block.
    const info = document.getElementById(`ProductInfo-${sectionId}`) || document;
    const price = info.querySelector('[data-er-price]');
    if (price && price.dataset.static !== 'true') {
      setText(price.querySelector('[data-er-now]'), d.price);
      setText(price.querySelector('[data-er-was]'), d.was);
      const save = price.querySelector('[data-er-save]');
      if (save) {
        save.hidden = !d.save;
        const t = save.querySelector('[data-er-save-text]');
        if (t) t.textContent = d.save || '';
      }
      const each = price.querySelector('[data-er-each]');
      if (each) setText(each, d.each ? `${d.each} ${each.dataset.label || ''}`.trim() : '');
      // Delivery line follows the pack: free from the theme threshold, fee below it.
      const ship = price.querySelector('[data-er-ship]');
      if (ship && d.freeShip) {
        const free = d.freeShip === 'true';
        ship.classList.toggle('is-fee', !free);
        const t = ship.querySelector('[data-er-ship-text]');
        if (t) t.textContent = free ? ship.dataset.freeText : ship.dataset.feeText;
      }
    }

    // Price inside the add to cart button.
    const btn = form.querySelector('.product-form__submit');
    if (btn && price && price.dataset.inButton === 'true') {
      let slot = btn.querySelector('.er-atc-price');
      if (!slot) {
        slot = document.createElement('span');
        slot.className = 'er-atc-price';
        slot.innerHTML = '<span data-now></span><s data-was></s>';
        btn.appendChild(slot);
        btn.classList.add('has-er-price');
      }
      slot.querySelector('[data-now]').textContent = d.price;
      setText(slot.querySelector('[data-was]'), d.was);
    }

    // Gift bar + stock line.
    info.querySelectorAll('[data-er-giftbar]').forEach((bar) => {
      bar.dataset.level = d.qty || '1';
      const msg = bar.querySelector('[data-er-giftbar-msg]');
      if (msg) msg.textContent = bar.getAttribute(`data-msg-${d.qty}`) || '';
    });
    info.querySelectorAll('[data-er-stock]').forEach((el) => (el.hidden = el.dataset.erStock !== input.value));

    // Legacy Dawn price box (if a Dawn price block is still in the template).
    const priceBox = document.getElementById(`price-${sectionId}`);
    if (priceBox) {
      priceBox.querySelectorAll('.price-item--regular, .price-item--sale').forEach((el) => (el.textContent = d.price));
    }

    input
      .closest('[data-er-bundles]')
      .querySelectorAll('.er-bundle')
      .forEach((card) => card.classList.toggle('is-selected', card.contains(input)));
    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', input.value);
      window.history.replaceState({}, '', url);
    }
    document.dispatchEvent(
      new CustomEvent('er:variant', {
        detail: { sectionId, id: input.value, price: d.price, was: d.was, save: d.saveShort, title: d.title, qty: d.qty },
      })
    );
  };

  const initBundles = () => {
    document.querySelectorAll('[data-er-bundles]').forEach((box) => {
      const sectionId = box.dataset.section;
      // Featured product (homepage) keeps the page URL as it is.
      const updateUrl = box.closest('product-info')?.dataset.updateUrl !== 'false';
      const checked = box.querySelector('input[type="radio"]:checked');
      if (checked) setVariant(sectionId, checked, { updateUrl: false });
      // Back/forward (bfcache) restores the radio but not our price/form state.
      window.addEventListener('pageshow', () => {
        const c = box.querySelector('input[type="radio"]:checked');
        if (c) setVariant(sectionId, c, { updateUrl: false });
      });
      box.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"]')) {
          setVariant(sectionId, e.target, { updateUrl });
          // Show this pack's image in the gallery (Dawn's own media-gallery API).
          const gallery = document.getElementById(`MediaGallery-${sectionId}`);
          if (gallery?.setActiveMedia && e.target.dataset.media) {
            try {
              gallery.setActiveMedia(`${sectionId}-${e.target.dataset.media}`, false);
            } catch (err) {
              /* gallery not ready */
            }
          }
          const card = e.target.closest('.er-bundle');
          if (canAnimate && card) M.animate(card, { scale: [0.98, 1] }, { duration: 0.25, ease: 'easeOut' });
        }
      });
    });
  };

  /* ---------- Add-ons: added first, then Shopify's own form continues ---------- */
  const initAddon = () => {
    document.addEventListener(
      'submit',
      async (e) => {
        const form = e.target;
        if (!form.matches || !form.matches('form[id^="product-form-"]') || form.dataset.erAddonsDone) return;
        // form.id would return the <input name="id"> inside the form.
        const sectionId = (form.getAttribute('id') || '').replace('product-form-', '');
        const scope = document.getElementById(`ProductInfo-${sectionId}`);
        if (!scope) return;
        const checks = [...scope.querySelectorAll('.er-addon__check:checked[data-er-addon]')];
        if (!checks.length) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        try {
          await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: checks.map((c) => ({ id: Number(c.dataset.erAddon), quantity: 1 })) }),
          });
        } catch (err) {
          /* the brace itself must still be added */
        }
        form.dataset.erAddonsDone = '1';
        if (form.requestSubmit) form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        delete form.dataset.erAddonsDone;
      },
      true
    );
  };

  /* ---------- Cart drawer: 3-pack upgrade + one-click upsell ---------- */
  const initCartActions = () => {
    const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
    document.addEventListener('click', async (e) => {
      const up = e.target.closest('[data-er-upgrade]');
      const add = e.target.closest('[data-er-quick-add]');
      const btn = up || add;
      if (!btn || btn.disabled) return;
      e.preventDefault();
      btn.disabled = true;
      btn.classList.add('is-loading');
      const root = window.Shopify?.routes?.root || '/';
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender ? drawer.getSectionsToRender().map((x) => x.id) : [];
      try {
        const id = Number(up ? up.dataset.variant : add.dataset.erQuickAdd);
        const item = { id, quantity: 1 };
        if (up && up.dataset.includes) item.properties = { Includes: up.dataset.includes };
        // Swap = add the new pack first, only then remove the old line, so a
        // failed add never empties the cart.
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: json,
          body: JSON.stringify({ items: [item], sections: up ? undefined : sections.join(','), sections_url: window.location.pathname }),
        });
        if (!res.ok) throw new Error('add failed');
        let state = await res.json();
        if (up) {
          const r = await fetch(`${root}cart/change.js`, {
            method: 'POST',
            headers: json,
            body: JSON.stringify({ id: up.dataset.key, quantity: 0, sections: sections.join(','), sections_url: window.location.pathname }),
          });
          if (!r.ok) throw new Error('change failed');
          state = await r.json();
        }
        document.dispatchEvent(new CustomEvent('er:cart-changed'));
        const onCartPage = window.location.pathname.replace(root, '/').startsWith('/cart');
        if (drawer && state.sections && !onCartPage) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(state);
        }
        else window.location.reload();
      } catch (err) {
        window.location.reload();
      }
    });
  };

  /* ---------- Sticky add to cart ---------- */
  const initSticky = () => {
    const bar = document.querySelector('[data-er-sticky]');
    if (!bar) return;
    const mainBtn = document.querySelector('.product-form__submit');
    if (!mainBtn) return;
    const priceEl = bar.querySelector('[data-er-sticky-price]');
    const wasEl = bar.querySelector('[data-er-sticky-was]');
    const saveEl = bar.querySelector('[data-er-sticky-save]');
    const labelEl = bar.querySelector('[data-er-sticky-variant]');
    document.addEventListener('er:variant', (e) => {
      if (priceEl) priceEl.textContent = e.detail.price;
      if (labelEl) labelEl.textContent = e.detail.title;
      setText(wasEl, e.detail.was);
      setText(saveEl, e.detail.save);
    });
    bar.querySelector('[data-er-sticky-btn]')?.addEventListener('click', () => mainBtn.click());
    let shown = false;
    const show = (on) => {
      if (on === shown) return;
      shown = on;
      bar.classList.toggle('is-visible', on);
      document.documentElement.style.setProperty('--er-bar-h', on ? `${Math.round(bar.getBoundingClientRect().height)}px` : '0px');
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      bar.querySelectorAll('button, a').forEach((el) => (el.tabIndex = on ? 0 : -1));
    };
    // Visible once the main button has scrolled past, hidden again while the
    // final CTA or the footer is on screen (they carry their own buttons).
    let pastMain = false;
    let endInView = false;
    const update = () => show(pastMain && !endInView);
    new IntersectionObserver(
      ([entry]) => {
        pastMain = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        update();
      },
      { threshold: 0 }
    ).observe(mainBtn);
    // Also on scroll: an anchor jump or restored scroll never triggers the observer.
    const check = () => {
      pastMain = mainBtn.getBoundingClientRect().bottom < 0;
      update();
    };
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('load', check);
    check();
    const ends = document.querySelectorAll('.er-final-cta, .footer, .er-ftrust');
    if (ends.length) {
      const visible = new Set();
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => (en.isIntersecting ? visible.add(en.target) : visible.delete(en.target)));
        endInView = visible.size > 0;
        update();
      });
      ends.forEach((el) => io.observe(el));
    }
  };

  /* ---------- Review highlights (scroll-snap track + arrows) ---------- */
  const initReviewsMini = () => {
    document.querySelectorAll('[data-er-revmini]').forEach((box) => {
      const track = box.querySelector('[data-er-rev-track]');
      if (!track) return;
      const step = (dir) => {
        const card = track.querySelector('.er-revmini__card');
        const w = card ? card.getBoundingClientRect().width + 12 : track.clientWidth;
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
        if (dir > 0 && atEnd) track.scrollTo({ left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
        else track.scrollBy({ left: dir * w, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      box.querySelector('[data-er-rev-prev]')?.addEventListener('click', () => step(-1));
      box.querySelector('[data-er-rev-next]')?.addEventListener('click', () => step(1));
    });
  };

  /* ---------- Review wall: sport filter chips ---------- */
  const initReviewFilters = () => {
    document.querySelectorAll('[data-er-review-filters]').forEach((bar) => {
      const grid = bar.closest('section')?.querySelector('[data-er-review-grid]');
      if (!grid) return;
      bar.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-filter]');
        if (!chip) return;
        const f = chip.dataset.filter;
        bar.querySelectorAll('[data-filter]').forEach((c) => {
          const on = c === chip;
          c.classList.toggle('is-active', on);
          c.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        grid.querySelectorAll('.er-review').forEach((r) => {
          r.hidden = f !== 'all' && r.dataset.sport !== f;
          r.style.opacity = '';
          r.style.transform = '';
        });
        // A filter shows every match; the "show all" button is no longer needed.
        grid.classList.remove('is-collapsed');
        bar.closest('section')?.querySelector('[data-er-reviews-more]')?.setAttribute('hidden', '');
      });
    });
    // Collapsed wall: 3 reviews on phones, 6 on desktop (CSS); the button opens the rest.
    document.querySelectorAll('[data-er-reviews-more]').forEach((btn) => {
      const grid = btn.closest('section')?.querySelector('[data-er-review-grid]');
      if (!grid) return;
      const limit = window.matchMedia('(max-width: 749px)').matches ? 3 : 6;
      if (grid.querySelectorAll('.er-review').length <= limit) {
        grid.classList.remove('is-collapsed');
        btn.hidden = true;
        return;
      }
      btn.addEventListener('click', () => {
        grid.classList.remove('is-collapsed');
        btn.hidden = true;
      });
    });
  };

  /* ---------- UGC clips: play in view (muted), tap for sound, arrows ---------- */
  const initUgc = () => {
    document.querySelectorAll('[data-er-ugc]').forEach((sec) => {
      const track = sec.querySelector('[data-er-ugc-track]');
      const videos = sec.querySelectorAll('video');
      if ('IntersectionObserver' in window && !reduceMotion) {
        const io = new IntersectionObserver(
          (entries) =>
            entries.forEach((en) => {
              const v = en.target;
              if (en.isIntersecting) v.play().catch(() => {});
              else v.pause();
            }),
          { threshold: 0.6 }
        );
        videos.forEach((v) => io.observe(v));
      }
      sec.querySelectorAll('[data-er-ugc-sound]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const v = btn.parentElement.querySelector('video');
          if (!v) return;
          const on = v.muted;
          videos.forEach((o) => (o.muted = true));
          sec.querySelectorAll('[data-er-ugc-sound]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
          v.muted = !on;
          if (on) v.play().catch(() => {});
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          btn.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
        });
      });
      const step = (dir) => {
        const card = track?.querySelector('.er-ugc__card');
        if (!card) return;
        track.scrollBy({ left: dir * (card.getBoundingClientRect().width + 16), behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      sec.querySelector('[data-er-ugc-prev]')?.addEventListener('click', () => step(-1));
      sec.querySelector('[data-er-ugc-next]')?.addEventListener('click', () => step(1));
    });
  };

  /* ---------- Motion: reveals, count-ups, timeline, staggered callouts ---------- */
  const initMotion = () => {
    const countUp = (el) => {
      const target = parseFloat(el.dataset.erCount);
      const decimals = (el.dataset.erCount.split('.')[1] || '').length;
      const suffix = el.dataset.erSuffix || '';
      if (!canAnimate) {
        el.textContent = target.toFixed(decimals) + suffix;
        return;
      }
      M.animate(0, target, {
        duration: 1.4,
        ease: [0.22, 1, 0.36, 1],
        onUpdate: (v) => (el.textContent = v.toFixed(decimals) + suffix),
      });
    };

    document.querySelectorAll('[data-er-count]').forEach((el) => {
      if (!canAnimate) return countUp(el);
      M.inView(el, () => countUp(el), { amount: 0.6 });
    });

    // Timeline: steps light up as they reach the middle of the screen.
    document.querySelectorAll('[data-er-timeline]').forEach((tl) => {
      const steps = tl.querySelectorAll('.er-timeline__step');
      const bar = tl.querySelector('.er-timeline__progress');
      if (reduceMotion || !('IntersectionObserver' in window)) {
        steps.forEach((s) => s.classList.add('is-active'));
        if (bar) bar.style.transform = 'scaleY(1)';
        return;
      }
      const io = new IntersectionObserver(
        (entries) => entries.forEach((en) => en.isIntersecting && en.target.classList.add('is-active')),
        { rootMargin: '0px 0px -45% 0px' }
      );
      steps.forEach((s) => io.observe(s));
      if (bar && canAnimate) {
        M.scroll(M.animate(bar, { scaleY: [0, 1] }, { ease: 'linear' }), {
          target: tl.querySelector('.er-timeline__list'),
          offset: ['start 65%', 'end 55%'],
        });
      } else if (bar) {
        bar.style.transform = 'scaleY(1)';
      }
    });

    if (!canAnimate) return;

    // Section reveals (headings + blocks), one gentle rise per element.
    document.querySelectorAll('[data-er-reveal]').forEach((el) => {
      el.style.opacity = '0';
      M.inView(
        el,
        () => {
          M.animate(el, { opacity: [0, 1], y: [24, 0] }, { duration: 0.6, ease: [0.22, 1, 0.36, 1] });
        },
        { amount: 0.25 }
      );
    });

    // Staggered groups (feature halo callouts, cards).
    document.querySelectorAll('[data-er-stagger]').forEach((group) => {
      const items = group.querySelectorAll(':scope > *');
      items.forEach((i) => (i.style.opacity = '0'));
      M.inView(
        group,
        () => {
          M.animate(items, { opacity: [0, 1], y: [16, 0] }, { duration: 0.5, delay: M.stagger(0.07), ease: 'easeOut' });
        },
        { amount: 0.2 }
      );
    });
  };

  /* ---------- Checkout button price: follows every cart change ---------- */
  const syncCheckoutTotals = async () => {
    try {
      const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
      const txt = new Intl.NumberFormat('en-GB', { style: 'currency', currency: cart.currency || 'GBP' }).format(cart.total_price / 100);
      document.querySelectorAll('.er-checkout-total').forEach((el) => (el.textContent = txt));
    } catch (e) {
      /* offline: leave as is */
    }
  };
  const initCheckoutTotals = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(syncCheckoutTotals, 300));
    document.addEventListener('er:cart-changed', syncCheckoutTotals);
  };

  /* ---------- Final CTA: same drawer add as the main button, one add per tap ---------- */
  const initFinalCta = () => {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest && e.target.closest('.er-final-cta__form');
      if (!form) return;
      e.preventDefault();
      if (form.dataset.busy) return;
      form.dataset.busy = '1';
      const btn = form.querySelector('[type="submit"]');
      btn?.classList.add('is-loading');
      const drawer = document.querySelector('cart-drawer');
      const fd = new FormData(form);
      if (drawer?.getSectionsToRender) {
        fd.append('sections', drawer.getSectionsToRender().map((x) => x.id).join(','));
        fd.append('sections_url', window.location.pathname);
      }
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
        if (!res.ok) throw new Error('add failed');
        const state = await res.json();
        if (drawer && state.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(state);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'er-final-cta', cartData: state });
        } else window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      } catch (err) {
        form.submit();
      } finally {
        btn?.classList.remove('is-loading');
        setTimeout(() => delete form.dataset.busy, 800);
      }
    });
  };

  /* ---------- Urgency: launch clock, cart hold, batch counter, orders line ----------
     One 1-second ticker for everything, so parts the cart drawer re-renders
     pick up the right values straight away. */
  const initUrgency = () => {
    const ls = {
      get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } },
      set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } },
    };
    const hash = (str) => {
      let h = 2166136261;
      for (let i = 0; i < str.length; i += 1) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
      return h >>> 0;
    };
    const HOLD_KEY = 'er-hold-t0';
    const tick = () => {
      const { date, seconds } = ukParts();
      const left = 86400 - seconds;
      const h = Math.floor(left / 3600);
      const m = Math.floor((left % 3600) / 60);
      document.querySelectorAll('[data-er-clock]').forEach((el) => (el.textContent = `${pad(h)}:${pad(m)}:${pad(left % 60)}`));
      document.querySelectorAll('[data-er-clock-hm]').forEach((el) => (el.textContent = `${h}h ${pad(m)}m`));

      // Launch batch: from start (+0-4 per day) down to min over the UK day.
      const day = iso(date);
      document.querySelectorAll('[data-er-batch]').forEach((el) => {
        const min = Number(el.dataset.min) || 4;
        const start = (Number(el.dataset.start) || 34) + (hash(day) % 5);
        const n = Math.max(min, Math.round(start - (start - min) * Math.pow(seconds / 86400, 0.85)));
        const out = el.querySelector('[data-er-batch-n]');
        if (out) out.textContent = n;
      });
      document.querySelectorAll('[data-er-sold]').forEach((el) => {
        const lo = Number(el.dataset.min) || 27;
        const hi = Math.max(lo, Number(el.dataset.max) || 48);
        const out = el.querySelector('[data-er-sold-n]');
        if (out) out.textContent = lo + (hash(`sold${day}`) % (hi - lo + 1));
      });

      // Cart hold for everyone: starts when first seen and simply starts a new
      // round when it reaches zero (Pablo, 4 Oct: loop smoothly, never "expired").
      const holds = document.querySelectorAll('[data-er-hold]');
      if (holds.length) {
        const ms = (Number(holds[0].dataset.min) || 10) * 60000;
        let t0 = Number(ls.get(HOLD_KEY));
        const token = holds[0].dataset.token || '';
        const now = Date.now();
        if (!t0 || ls.get('er-hold-token') !== token || t0 > now) {
          t0 = now;
          ls.set('er-hold-token', token);
        }
        if (now - t0 >= ms) t0 = now - ((now - t0) % ms); // next round, same rhythm
        ls.set(HOLD_KEY, String(t0));
        const rest = Math.max(0, t0 + ms - now);
        holds.forEach((el) => {
          const time = el.querySelector('[data-er-hold-time]');
          const msg = el.querySelector('[data-er-hold-msg]');
          if (time) time.textContent = `${pad(Math.floor(rest / 60000))}:${pad(Math.floor((rest % 60000) / 1000))}`;
          if (msg) msg.textContent = el.dataset.on;
          el.classList.remove('is-expired');
        });
      }
    };
    tick();
    setInterval(tick, 1000);
  };

  const init = () => {
    // Sticky first: it listens for the er:variant event that initBundles
    // fires for the pre-selected pack.
    initSticky();
    initBundles();
    initAddon();
    initCartActions();
    initReviewsMini();
    initReviewFilters();
    initUgc();
    initMotion();
    initUrgency();
    initCheckoutTotals();
    initFinalCta();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* er-modal-tapclose: on phones a tap on the zoomed photo (or the dark area) closes Dawn's product modal. */
document.addEventListener('click', (e) => {
  if (window.innerWidth >= 750) return;
  const modal = e.target.closest && e.target.closest('product-modal[open]');
  if (!modal || e.target.closest('.product-media-modal__toggle, video, model-viewer, iframe')) return;
  if (typeof modal.hide === 'function') modal.hide();
  else modal.querySelector('.product-media-modal__toggle')?.click();
});

/* er-menu-anchor: a menu link to a section on the page you are already on
   closes the menu first, then scrolls smoothly to the section (just below
   the sticky header). Links to other pages behave normally. */
document.addEventListener('click', (e) => {
  const a = e.target.closest && e.target.closest('a[href*="#"]');
  if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey) return;
  let url;
  try { url = new URL(a.href, location.href); } catch (err) { return; }
  if (url.pathname !== location.pathname || !url.hash || url.hash === '#') return;
  const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
  if (!target) return;
  e.preventDefault();
  const drawer = a.closest('header-drawer, menu-drawer');
  const details = drawer && drawer.querySelector('details[open]');
  if (details) {
    const summary = details.querySelector('summary');
    try { drawer.closeMenuDrawer(e, summary); } catch (err) { /* fall through */ }
    details.removeAttribute('open');
    summary && summary.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop');
    document.querySelector('.section-header .header')?.classList.remove('menu-open');
  }
  setTimeout(() => {
    const head = document.querySelector('.section-header');
    const off = (head && getComputedStyle(head).position !== 'relative' ? head.getBoundingClientRect().height : 0) + 12;
    const y = target.getBoundingClientRect().top + window.scrollY - off;
    const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.__erAnchorScroll = Date.now();
    window.scrollTo({ top: Math.max(0, y), behavior: smooth ? 'smooth' : 'auto' });
    history.replaceState(null, '', url.hash);
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }, details ? 350 : 0);
});
